import React, { useLayoutEffect, useRef, useState } from 'react';

// Keeps a game board on one screen with no scrollbars: when the board's
// natural size is taller (or wider) than the space left in the game modal,
// it is scaled down uniformly to fit, and the wrapper takes the scaled
// height so no empty gap is left behind. Boards that already fit stay at
// 100%.
//
// Games that turn mouse positions into board coordinates divide by the
// rendered/natural size ratio (getBoundingClientRect().width / offsetWidth),
// so they keep working while scaled.
// bottomGap: room kept free below the board inside the modal body (the
// board's own bottom padding). viewportGap: room kept free below it in the
// window (that padding plus the modal's bottom margin), so the page behind
// never scrolls either.
export const GameFitBox = ({ children, bottomGap = 16, viewportGap = 64 }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [fit, setFit] = useState({ scale: 1, height: null });

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;

    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // offsetWidth/offsetHeight ignore transforms, so this is the
        // board's natural (unscaled) size.
        const naturalH = inner.offsetHeight;
        const naturalW = inner.offsetWidth;
        if (!naturalH || !naturalW) return;

        const body = outer.closest('.modal-body');
        let available = window.innerHeight - outer.getBoundingClientRect().top - viewportGap;
        if (body) {
          const cap = parseFloat(window.getComputedStyle(body).maxHeight);
          if (Number.isFinite(cap)) {
            const offsetInBody = outer.getBoundingClientRect().top - body.getBoundingClientRect().top;
            available = Math.min(available, cap - offsetInBody - bottomGap);
          }
        }
        const widthAvailable = outer.parentElement ? outer.parentElement.clientWidth : naturalW;

        const scale = Math.max(0.3, Math.min(1, available / naturalH, widthAvailable / naturalW));
        const height = Math.ceil(naturalH * scale);
        setFit((prev) => (
          Math.abs(prev.scale - scale) < 0.005 && prev.height === height ? prev : { scale, height }
        ));
      });
    };

    const ro = new ResizeObserver(measure);
    ro.observe(inner);
    window.addEventListener('resize', measure);
    measure();
    // The modal slides in with a short transform animation; measure again
    // once it has settled so the top offset is the final one.
    const settle = setTimeout(measure, 600);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [bottomGap, viewportGap]);

  return (
    <div
      ref={outerRef}
      className="game-fit-box"
      style={{ width: '100%', height: fit.height ?? 'auto' }}
    >
      <div
        ref={innerRef}
        style={{
          width: '100%',
          transform: fit.scale < 1 ? `scale(${fit.scale})` : undefined,
          transformOrigin: 'top center',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default GameFitBox;
